import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { articlesBySlug, articles } from "@/mocks/articles";
import { siteInfo } from "@/mocks/site";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const article = slug ? articlesBySlug[slug] : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            datePublished: article.date,
            dateModified: article.date,
            author: {
              "@type": "Organization",
              name: article.author,
            },
            publisher: {
              "@type": "Organization",
              name: siteInfo.name,
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${siteInfo.url}/article/${article.slug}`,
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                {
                  label: article.categoryName,
                  path: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />

            <div className="py-6 md:py-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap hover:bg-primary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line"></i> {article.likes} 点赞
                </span>
              </div>

              <div className="my-6 md:my-8 rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto max-h-[500px] object-cover rounded-lg"
                />
              </div>

              <div
                className="article-content max-w-none"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-10 md:py-14 mt-8">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 md:mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200"
                    >
                      <div className="relative h-44 md:h-48 overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-2">{related.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}